import { all, call, put, takeEvery, CallEffect, PutEffect } from 'redux-saga/effects';
import { RequestActionType, requestError, requestSuccess } from 'commons/src/actions/requestActions';
import { Rating, SensorTypes } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import RequestActions from 'commons/src/models/RequestTypes';
import { toErrorType } from 'commons/src/sagas/isErrorType';
import {
    FetchIaqInsightData,
    FetchIaqInsightDataSuccess,
    fetchIaqInsightDataSuccess,
    FetchIaqInsightDataV2,
    FetchRadonInsightData,
    FetchRadonInsightDataSuccess,
    fetchRadonInsightDataSuccess,
    FetchVirusRiskInsightData,
    FetchVirusRiskInsightDataSuccess,
    fetchVirusRiskInsightDataSuccess,
    IaqInsightResponse,
    InsightActionType,
    RadonInsightResponse,
    VirusRiskInsightResponse,
} from '../actions/insightActions';
import getInsightData, { getIaqReport, getIaqReportV2, getVirusRiskReport } from '../api/insightApi';
import { IaqInsightData, ReportSensorThresholds } from '../models/common';
import { BusinessRequestType as RequestType } from '../reducers/BusinessRequestType';

const VALID_RATINGS = new Set<string>(Object.values(Rating));

function isValidThresholdRange(r: unknown): r is ThresholdRange {
    if (!r || typeof r !== 'object' || !('rating' in r)) return false;
    const range = r as Record<string, unknown>;
    if (typeof range.rating !== 'string' || !VALID_RATINGS.has(range.rating)) return false;
    if ('from' in range && range.from !== undefined && typeof range.from !== 'number') return false;
    if ('to' in range && range.to !== undefined && typeof range.to !== 'number') return false;
    return true;
}

function parseThresholdRanges(ranges: unknown): ThresholdRange[] {
    if (!Array.isArray(ranges)) return [];
    return ranges.filter((r): r is ThresholdRange => isValidThresholdRange(r));
}

type ParsedApiThreshold = { sensorType?: string; unit: string; ranges: ThresholdRange[] };

function parseApiThreshold(raw: unknown): ParsedApiThreshold | null {
    if (!raw || typeof raw !== 'object') return null;
    const obj = raw as Record<string, unknown>;
    if (typeof obj.unit !== 'string') return null;
    return {
        sensorType: typeof obj.sensorType === 'string' ? obj.sensorType : undefined,
        unit: obj.unit,
        ranges: parseThresholdRanges(obj.ranges),
    };
}

type GetRadonInsightDataSagaReturnType = Generator<
    CallEffect<RadonInsightResponse> | PutEffect<FetchRadonInsightDataSuccess> | RequestActions,
    void,
    RadonInsightResponse
>;
export function* getRadonInsightDataSaga({ payload }: FetchRadonInsightData): GetRadonInsightDataSagaReturnType {
    const { from, to, serialNumbers, locationId } = payload;
    try {
        const response: RadonInsightResponse = yield call(getInsightData, {
            from,
            to,
            serialNumbers,
            locationId,
        });
        yield put(fetchRadonInsightDataSuccess({ ...response, ...payload }));
        yield put(requestSuccess(RequestType.FetchRadonInsightData, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.FetchRadonInsightData, RequestActionType.Error));
    }
}

type GetIaqInsightDataSagaReturnType = Generator<
    CallEffect<IaqInsightResponse> | PutEffect<FetchIaqInsightDataSuccess> | RequestActions,
    void,
    IaqInsightResponse
>;
export function* getIaqInsightDataSaga({ payload }: FetchIaqInsightData): GetIaqInsightDataSagaReturnType {
    try {
        const response: IaqInsightResponse = yield call(getIaqReport, payload);
        yield put(fetchIaqInsightDataSuccess({ ...payload, ...response }));
        yield put(requestSuccess(RequestType.FetchIaqInsightData, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.FetchIaqInsightData, RequestActionType.Error));
    }
}

// Used by iaqInsight2 feature: saga that handles v2 API response transformation
// The v2 API returns thresholds and spaceOpeningHoursValues nested inside each sensor,
// so this saga transforms them to match the expected structure used by the UI components
type GetIaqInsightDataV2SagaReturnType = Generator<
    CallEffect<IaqInsightResponse> | PutEffect<FetchIaqInsightDataSuccess> | RequestActions,
    void,
    IaqInsightResponse
>;
export function* getIaqInsightDataV2Saga({ payload }: FetchIaqInsightDataV2): GetIaqInsightDataV2SagaReturnType {
    try {
        const response: IaqInsightResponse = yield call(getIaqReportV2, payload);

        // The API v2 structure has thresholds and spaceOpeningHoursValues INSIDE each sensor
        // We need to extract them and transform to the expected structure
        const rawSensorReports = response.sensorReports || {};
        const transformedSensorReports: { [sensorName: string]: IaqInsightData } = {};
        const transformedThresholds: { [sensor: string]: ReportSensorThresholds } = {};

        // Transform each sensor's data
        // Map 'sla' back to 'noise' for frontend consistency
        Object.keys(rawSensorReports).forEach(sensorName => {
            const mappedSensorName = sensorName === 'sla' ? 'noise' : sensorName;
            const sensorData = rawSensorReports[sensorName];

            // Extract and validate threshold from inside the sensor data
            const rawThresholds = (sensorData as { thresholds?: unknown }).thresholds;
            const sensorThreshold = parseApiThreshold(rawThresholds);
            if (sensorThreshold) {
                transformedThresholds[mappedSensorName] = {
                    type: (sensorThreshold.sensorType || mappedSensorName) as SensorTypes,
                    unit: sensorThreshold.unit,
                    ranges: sensorThreshold.ranges,
                };
            }

            const spaceOpeningHoursValues =
                ((sensorData as { spaceOpeningHoursValues?: unknown }).spaceOpeningHoursValues as
                    | Record<
                          string,
                          {
                              name?: string;
                              floor?: number;
                              roomType?: string;
                              average?: number;
                              highestMeasurement?: number;
                              lowestMeasurement?: number;
                              thresholds?: { sensorType?: string; unit: string; ranges: ThresholdRange[] };
                          }
                      >
                    | undefined) || {};

            const devicesOpeningHoursValues: {
                [spaceId: string]: {
                    name?: string;
                    floor?: number;
                    roomType?: string;
                    average?: number;
                    highestMeasurement?: number;
                    lowestMeasurement?: number;
                    thresholds?: ReportSensorThresholds;
                };
            } = {};

            Object.keys(spaceOpeningHoursValues).forEach(spaceId => {
                const spaceData = spaceOpeningHoursValues[spaceId];
                if (spaceData && typeof spaceData === 'object') {
                    let spaceThresholds: ReportSensorThresholds | undefined;
                    const parsedSpaceThreshold = parseApiThreshold(spaceData.thresholds);
                    if (parsedSpaceThreshold) {
                        spaceThresholds = {
                            type: (parsedSpaceThreshold.sensorType || mappedSensorName) as SensorTypes,
                            unit: parsedSpaceThreshold.unit,
                            ranges: parsedSpaceThreshold.ranges,
                        };
                    }

                    const hasAverage =
                        'average' in spaceData &&
                        spaceData.average !== undefined &&
                        spaceData.average !== null &&
                        !Number.isNaN(spaceData.average);

                    devicesOpeningHoursValues[spaceId] = {
                        name: spaceData.name,
                        floor: spaceData.floor,
                        roomType: spaceData.roomType,
                        average: hasAverage ? spaceData.average : undefined,
                        highestMeasurement: spaceData.highestMeasurement,
                        lowestMeasurement: spaceData.lowestMeasurement,
                        thresholds: spaceThresholds,
                    };
                }
            });

            transformedSensorReports[mappedSensorName] = {
                averageGraph: sensorData.averageGraph || [],
                averageValueWithinOpeningHours: sensorData.averageValueWithinOpeningHours ?? 0,
                averageValue: sensorData.averageValue ?? 0,
                unit: sensorThreshold?.unit || (sensorData as { unit?: string }).unit || 'ppm',
                devicesOpeningHoursValues,
            };
        });

        const safeResponse: IaqInsightResponse = {
            ...response,
            sensorReports: transformedSensorReports,
            thresholds: transformedThresholds,
            serialNumbers: response.serialNumbers || [],
            locationId: response.locationId || payload.locationId,
            reportId: response.reportId || '',
        };

        yield put(fetchIaqInsightDataSuccess({ ...payload, ...safeResponse }));
        yield put(requestSuccess(RequestType.FetchIaqInsightData, RequestActionType.Success));
    } catch (error) {
        const raw = toErrorType(error);
        // API may return { error, description }; normalize so store has error_description for display
        const errorAsErrorType =
            typeof raw === 'object' &&
            raw !== null &&
            'description' in raw &&
            typeof (raw as { description?: string }).description === 'string'
                ? { ...raw, error_description: (raw as { description: string }).description }
                : raw;
        yield put(requestError(errorAsErrorType, RequestType.FetchIaqInsightData, RequestActionType.Error));
    }
}

type GetVirusRiskDataSagaReturnType = Generator<
    CallEffect<VirusRiskInsightResponse> | PutEffect<FetchVirusRiskInsightDataSuccess> | RequestActions,
    void,
    VirusRiskInsightResponse
>;
export function* getVirusRiskInsightDataSaga({ payload }: FetchVirusRiskInsightData): GetVirusRiskDataSagaReturnType {
    const { locationId, includeLogo, serialNumbers } = payload;
    try {
        const response: VirusRiskInsightResponse = yield call(getVirusRiskReport, payload);
        yield put(fetchVirusRiskInsightDataSuccess({ locationId, includeLogo, serialNumbers, ...response }));
        yield put(requestSuccess(RequestType.FetchVirusRiskInsightData, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.FetchVirusRiskInsightData, RequestActionType.Error));
    }
}

export default function* InsightSagas(): Generator {
    yield all([
        takeEvery(InsightActionType.FetchRadonInsightDataInit, getRadonInsightDataSaga),
        takeEvery(InsightActionType.FetchIaqInsightDataInit, getIaqInsightDataSaga),
        // Used by iaqInsight2 feature: register saga handler for v2 action
        takeEvery(InsightActionType.FetchIaqInsightDataV2Init, getIaqInsightDataV2Saga),
        takeEvery(InsightActionType.FetchVirusRiskInsightDataInit, getVirusRiskInsightDataSaga),
    ]);
}
